import type { AccessState } from '@lingoping/core';
import { isDevAuthMock } from '../../auth/index.js';

/**
 * Development preview: `?access=trial|trial_last|locked_trial|locked_payment|active|cancelled`
 * (also `subscribed_trial` and `locked_ended`) fakes the access state, so every screen can be
 * seen without a server. Only builds with the simulated sign-in honour it (never staging or
 * production, see `isDevAuthMock`).
 */
export const DEV_ACCESS_NAMES = [
  'trial',
  'trial_last',
  'subscribed_trial',
  'locked_trial',
  'locked_payment',
  'locked_ended',
  'active',
  'cancelled',
] as const;

export type DevAccessName = (typeof DEV_ACCESS_NAMES)[number];

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

function iso(now: Date, offsetMs: number): string {
  return new Date(now.getTime() + offsetMs).toISOString();
}

function build(name: DevAccessName, now: Date): AccessState {
  const base = {
    enforced: true,
    until: null,
    locked_since: null,
    subscription: null,
  };
  switch (name) {
    case 'trial':
      return {
        ...base,
        state: 'trial',
        reason: 'trial',
        until: iso(now, 8.5 * DAY),
        trial_ends_at: iso(now, 8.5 * DAY),
      };
    case 'trial_last':
      return {
        ...base,
        state: 'trial',
        reason: 'trial',
        until: iso(now, 6 * HOUR),
        trial_ends_at: iso(now, 6 * HOUR),
      };
    case 'subscribed_trial':
      return {
        ...base,
        state: 'active',
        reason: 'subscription',
        trial_ends_at: iso(now, 8.5 * DAY),
        subscription: {
          status: 'trialing',
          plan: 'monthly',
          current_period_end: iso(now, 8.5 * DAY),
          cancel_at: null,
        },
      };
    case 'locked_trial':
      return {
        ...base,
        state: 'locked',
        reason: 'trial_ended',
        trial_ends_at: iso(now, -2 * DAY),
        locked_since: iso(now, -2 * DAY),
      };
    case 'locked_payment':
      return {
        ...base,
        state: 'locked',
        reason: 'payment_failed',
        trial_ends_at: iso(now, -20 * DAY),
        locked_since: iso(now, -1 * DAY),
        subscription: {
          status: 'past_due',
          plan: 'monthly',
          current_period_end: iso(now, -1 * DAY),
          cancel_at: null,
        },
      };
    case 'locked_ended':
      return {
        ...base,
        state: 'locked',
        reason: 'subscription_ended',
        trial_ends_at: iso(now, -60 * DAY),
        locked_since: iso(now, -3 * DAY),
        subscription: {
          status: 'canceled',
          plan: 'monthly',
          current_period_end: iso(now, -3 * DAY),
          cancel_at: iso(now, -3 * DAY),
        },
      };
    case 'active':
      return {
        ...base,
        state: 'active',
        reason: 'subscription',
        trial_ends_at: iso(now, -40 * DAY),
        subscription: {
          status: 'active',
          plan: 'monthly',
          current_period_end: iso(now, 20 * DAY),
          cancel_at: null,
        },
      };
    case 'cancelled':
      return {
        ...base,
        state: 'active',
        reason: 'subscription',
        until: iso(now, 12 * DAY),
        trial_ends_at: iso(now, -40 * DAY),
        subscription: {
          status: 'canceled',
          plan: 'monthly',
          current_period_end: iso(now, 12 * DAY),
          cancel_at: iso(now, 12 * DAY),
        },
      };
  }
}

/** The faked state a query string asks for, or null (no or unknown value). Pure; for tests. */
export function devAccessFromSearch(search: string, now = new Date()): AccessState | null {
  const value = new URLSearchParams(search).get('access');
  return value && (DEV_ACCESS_NAMES as readonly string[]).includes(value)
    ? build(value as DevAccessName, now)
    : null;
}

/** The faked state for this page, or null outside the development build. */
export function devAccessOverride(): AccessState | null {
  if (!isDevAuthMock() || typeof window === 'undefined') return null;
  return devAccessFromSearch(window.location.search);
}
